import { Button, ButtonLevel, ButtonSize, TextSize } from '@vizit/atoms';
import { AlertCircleIcon } from '@vizit/icons';
import { Dialog, DialogHeaderStatus, DialogHeaderVariant, type DialogProps } from '@vizit/molecules';

/**
 * Which confirmation copy the dialog shows, chosen by the class-change
 * transition. `change` is the generic fallback — also used when scoring can't
 * actually change AND for a scored→scored reclassification (no dedicated
 * "re-score" modal; per Figma it folds into the generic copy). `reset` resets
 * to the original classification (GRE-55).
 */
type AssetClassDialogVariant = 'change' | 'score' | 'unscore' | 'reset';

interface ChangeAssetClassDialogProps extends Omit<DialogProps, 'children'> {
  /**
   * Which transition copy to show. Defaults to the generic `change` copy.
   */
  variant?: AssetClassDialogVariant;
  /**
   * Called when the user clicks the confirm button. The consumer fires the
   * actual mutation; this dialog stays presentational.
   */
  onConfirm: () => void;
}

const VARIANT_COPY: Record<
  Exclude<AssetClassDialogVariant, 'change'>,
  { title: string; body: string; confirm: string }
> = {
  score: {
    title: 'Change asset class',
    body: 'Reclassifying this as a marketing asset adds it to your Carousel score and could change your Product Page and Asset Mix scores. You can reclassify it any time.',
    confirm: 'Score asset',
  },
  unscore: {
    title: 'Change asset class',
    body: 'Reclassifying this as an informational asset removes it from your Carousel score and could change your Product Page and Asset Mix scores. You can reclassify it any time.',
    confirm: 'Unscore asset',
  },
  reset: {
    title: 'Reset asset class',
    body: 'This could change your Carousel, Asset Mix and Product Page scores. You can reclassify it any time.',
    confirm: 'Reset asset',
  },
};

function ChangeAssetClassDialog(props: ChangeAssetClassDialogProps) {
  const { isOpen = false, onOpenChange, variant = 'change', onConfirm, ...other } = props;

  const copy =
    variant === 'change'
      ? {
          title: 'Change asset class',
          body: 'This could change your Carousel, Asset Mix and Product Page scores. You can reclassify it any time.',
          confirm: 'Reclassify asset',
        }
      : VARIANT_COPY[variant];

  const handleCancel = () => onOpenChange?.(false);
  const handleConfirm = () => {
    onConfirm();
    onOpenChange?.(false);
  };

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} {...other}>
      <Dialog.Overlay>
        <Dialog.Content dialogClassName="gap-4">
          <Dialog.Header variant={DialogHeaderVariant.Icon} status={DialogHeaderStatus.Warning} icon={AlertCircleIcon}>
            <Dialog.Title className="font-serif">{copy.title}</Dialog.Title>
          </Dialog.Header>
          <div className="px-12">
            <Dialog.Subtitle elementType="p" size={TextSize.Sm} className="text-gray-utility-600">
              {copy.body}
            </Dialog.Subtitle>
          </div>
          <Dialog.Footer className="grid grid-cols-2 gap-6">
            <Button className="w-full" size={ButtonSize.Lg} level={ButtonLevel.Secondary} onPress={handleCancel}>
              Cancel
            </Button>
            <Button className="w-full" size={ButtonSize.Lg} level={ButtonLevel.Primary} onPress={handleConfirm}>
              {copy.confirm}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog>
  );
}

export { ChangeAssetClassDialog };
export type { ChangeAssetClassDialogProps, AssetClassDialogVariant };
